import { FileChooser } from "simplydo/react/components/Choosers/FileChooser/FileChooser";
import { IFrameChooser } from "simplydo/react/components/Choosers/IFrameChooser";
import { ImageChooser } from "simplydo/react/components/Choosers/ImageChooser";
import { LinkChooser } from "simplydo/react/components/Choosers/LinkChooser";
import { VideoChooser } from "simplydo/react/components/Choosers/VideoChooser";
import type { RemirrorMenuItem } from "simplydo/react/richtext/BasicRichText";
import {
  extractHTMLTag,
  iframeRegex,
} from "simplydo/react/richtext/Remirror/extensions/custom/iframe";

type ChooserState = Record<
  string,
  { open?: boolean; cmd?: unknown; text?: string } | undefined
>;

type SetChooser = (
  kind: string,
  open: boolean,
  cmd?: unknown,
  text?: string,
) => void;

export const getFullMenu = (
  chooser: ChooserState,
  setChooser: SetChooser,
  forType: string | undefined,
  forId: string | undefined,
): Array<RemirrorMenuItem> => [
  {
    name: "link",
    icon: "linkify",
    onClick: ({ commands, isActive, selectedText }) => {
      if (isActive) {
        commands.removeLink?.();
      } else {
        setChooser(
          "link",
          !chooser.link?.open,
          commands.createCustomLink,
          selectedText,
        );
      }
    },
    MenuItem: (({ RenderItem, itemProps }: any) => (
      <LinkChooser
        trigger={
          <div>
            <RenderItem {...itemProps} />
          </div>
        }
        isOpen={!!chooser.link?.open}
        shouldFix={false}
        selectedText={chooser.link?.text ?? ""}
        onClose={() => setChooser("link", false)}
        onComplete={(text, url) => {
          (chooser.link?.cmd as ((data: unknown) => void) | undefined)?.({
            text,
            href: url,
          });
          setChooser("link", false);
        }}
      />
    )) as any,
  },
  {
    name: "image",
    onClick: ({ commands, selectedText }) => {
      setChooser(
        "image",
        !chooser.image?.open,
        commands.insertImage,
        selectedText,
      );
    },
    MenuItem: (({ RenderItem, itemProps }: any) => (
      <ImageChooser
        trigger={
          <div>
            <RenderItem {...itemProps} />
          </div>
        }
        forType={forType ?? ""}
        forId={forId ?? ""}
        isOpen={!!chooser.image?.open}
        onClose={() => setChooser("image", false)}
        onComplete={(_image, _name, imageSrc) => {
          (chooser.image?.cmd as ((data: unknown) => void) | undefined)?.({
            src: imageSrc,
          });
          setChooser("image", false);
        }}
      />
    )) as any,
  },
  {
    name: "video",
    onClick: ({ commands, selectedText }) => {
      setChooser(
        "video",
        !chooser.video?.open,
        (embed: boolean) => (embed ? commands.addIframe : commands.insertVideo),
        selectedText,
      );
    },
    MenuItem: (({ RenderItem, itemProps }: any) => (
      <VideoChooser
        trigger={
          <div>
            <RenderItem {...itemProps} />
          </div>
        }
        forType={forType ?? ""}
        forId={forId ?? ""}
        isOpen={!!chooser.video?.open}
        onClose={() => setChooser("video", false)}
        onComplete={(video, _name, videoSrc) => {
          const isEmbed = video.startsWith("embed-");
          const src = isEmbed ? video.replace("embed-", "") : videoSrc;

          (
            chooser.video?.cmd as
              | ((embed: boolean) => ((data: unknown) => void) | undefined)
              | undefined
          )?.(isEmbed)?.({ src });
          setChooser("video", false);
        }}
      />
    )) as any,
  },
  {
    name: "file",
    onClick: ({ commands, isActive, selectedText }) => {
      if (isActive) return;
      setChooser(
        "file",
        !chooser.file?.open,
        commands.insertFile,
        selectedText,
      );
    },
    MenuItem: (({ RenderItem, itemProps }: any) => (
      <FileChooser
        trigger={
          <div>
            <RenderItem {...itemProps} />
          </div>
        }
        forType={forType ?? ""}
        forId={forId ?? ""}
        isOpen={!!chooser.file?.open}
        onClose={() => setChooser("file", false)}
        onComplete={(_file, name, fileSrc) => {
          (chooser.file?.cmd as ((data: unknown) => void) | undefined)?.({
            name,
            src: fileSrc,
          });
          setChooser("file", false);
        }}
      />
    )) as any,
  },
  {
    name: "iframe",
    icon: "file code",
    onClick: ({ commands, isActive, selectedText }) => {
      if (isActive) {
        commands.removeLink?.();
      } else {
        setChooser(
          "iframe",
          !chooser.iframe?.open,
          commands.addIframe,
          selectedText,
        );
      }
    },
    MenuItem: (({ RenderItem, itemProps }: any) => (
      <IFrameChooser
        trigger={
          <div>
            <RenderItem {...itemProps} />
          </div>
        }
        isOpen={!!chooser.iframe?.open}
        onClose={() => setChooser("iframe", false)}
        onComplete={(iFrameURL) => {
          // @ts-ignore
          const options: { src: string; title?: string; allow?: string } = {};
          const match = iFrameURL.match(iframeRegex);
          if (match) {
            options.src = extractHTMLTag(match[0], "src") || "";
            options.title = extractHTMLTag(match[0], "title") ?? "iFrame";
            options.allow = extractHTMLTag(match[0], "allow") ?? "";
          } else {
            options.src = iFrameURL;
          }

          if (options.src) {
            (chooser.iframe?.cmd as ((data: unknown) => void) | undefined)?.(
              options,
            );
          }
          setChooser("iframe", false);
        }}
      />
    )) as any,
  },
];
